import 'what-input';
// Include react-spring in commons.js, so that it doesn't need to be included in every page template
import '@react-spring/web';
import React from 'react';
import type { PageProps as GatsbyPageProps } from 'gatsby';
import type { DefaultTheme } from 'styled-components';
import { Provider as TooltipProvider } from '@radix-ui/react-tooltip';

import Navbar, { EXPANDED_LABEL_CLASSNAME } from '../components/Navbar';
import {
    CssColorVariableDefinitions,
    CssVariableDefinitions,
} from '../components/CSSVariableDefinitions';
import {
    defaultColorSchemes,
    Provider as ColorSchemeProvider,
} from '../components/ColorSchemeContext';
import { Provider as DebouncedCartUpdateProvider } from '../hooks/useDebouncedCartUpdateMutation';
import GlobalFonts from '../components/GlobalFonts';
import SiteOverlay from '../components/SiteOverlay';
import ErrorOverlay from '../components/ErrorOverlay';
import PageErrorBoundary from '../components/PageErrorBoundary';
import Footer, { FooterMinimal } from '../components/Footer';
import PageQueryContext from '../components/PageContext';
import type { BlogPostStubsQuery, EulasQuery } from '../gql/api-ssr';
import type { PageQueryContextType } from '../components/PageContext';
import WindowSizeCalc from '../components/WindowSizeCalc';
import { sentryMessage } from '../utils/sentry';
import Reset from '../components/Reset';
import Globals from '../components/Globals';
import type { Metadata as MetadataType } from '../data/metadata';
import GlobalRemoteFonts from '../components/GlobalRemoteFonts';
import { ApolloProvider } from '@apollo/client';
import runtimeApolloClient from '../utils/runtimeApolloClient';
import useHasMounted from '../hooks/useHasMounted';
import getColorThemeFromBaseColors from '../utils/getColorThemeFromBaseColors';
import { useGlobalState } from '../components/GlobalRuntimeState';
import type { NavLinkProps } from '../components/Navbar';
import { createHomeUrl } from '../utils/urlHelper';
import useFontFamilyGroupStubs from '../hooks/useFontFamilyGroupStubs';
import notPreRelease from '../utils/notPreRelease';

// Code-split a few things to prevent `commons.js` from getting too big
const Modal = React.lazy(() => import('../components/Modal'));
const AdminColorPicker = React.lazy(
    () => import('../components/AdminColorPicker'),
);
const GridOverlay = React.lazy(() => import('../components/GridOverlay'));

type BlogPostStub = NonNullable<BlogPostStubsQuery['ssr']['blogPosts']>[number];

export interface PageContext extends PageQueryContextType {
    breadcrumbs?: (NavLinkProps | NavLinkProps[] | NavLinkProps[][])[];
    colorSchemes?: DefaultTheme[];
    hideCartCheckoutButton?: boolean;
    isBuyOrCheckoutPage?: boolean;
    metadata?: MetadataType;
    isBuyPage?: boolean;
    isInUsePostPage?: boolean;
    blogPostStubs?: BlogPostStub[];
    relatedBlogPostStubs?: BlogPostStub[];
    noindex?: boolean;
    minimalFooter?: boolean;
    isPreviewPage?: boolean;
    subscriptionSuccess?: boolean;
    eula?: EulasQuery['ssr']['config']['eulas'][number];
    hasMounted?: boolean;
}

export interface KlimPageProps extends GatsbyPageProps {
    pageContext: PageContext;
}

function Layout({
    pageContext,
    children,
    location,
}: KlimPageProps): React.ReactElement | null {
    const {
        hideCartCheckoutButton,
        isBuyOrCheckoutPage,
        isBuyPage,
        metadata,
        minimalFooter,
        fontFamily,
        fontFamilyGroup,
        breadcrumbs,
    } = pageContext;
    const fontFamilyGroupStubs = useFontFamilyGroupStubs();
    const hasMounted = useHasMounted();

    if (!metadata) {
        sentryMessage(`Page ${location.pathname} lacks metadata!`);
    }

    // Count the group with the most families, to set the minimum height of the font blocks
    const maxFontFamilies = React.useMemo(
        () =>
            fontFamilyGroupStubs.reduce(
                (previousValue, currentValue) =>
                    Math.max(
                        previousValue,
                        currentValue.fontFamilies.filter(notPreRelease).length,
                    ),
                1,
            ),
        [fontFamilyGroupStubs],
    );

    return (
        <>
            <GlobalFonts />
            <GlobalRemoteFonts
                fontFamily={fontFamily}
                fontFamilyGroup={fontFamilyGroup}
            />
            <Reset />
            <CssVariableDefinitions
                $numBreadCrumbs={
                    (breadcrumbs && Array.isArray(breadcrumbs)
                        ? breadcrumbs.length
                        : 0) + 1
                }
                $maxFontFamilies={maxFontFamilies}
            />
            <Globals />
            <CssColorVariableDefinitions />

            <DebouncedCartUpdateProvider>
                <Navbar
                    breadcrumbs={[
                        {
                            url: createHomeUrl(),
                            label: (
                                <>
                                    Klim
                                    <span className={EXPANDED_LABEL_CLASSNAME}>
                                        &nbsp;Type Foundry
                                    </span>
                                </>
                            ),
                        },
                        ...(breadcrumbs || []),
                    ]}
                    fontFamily={fontFamily}
                    fontFamilyGroup={fontFamilyGroup}
                    hideCartCheckoutButton={hideCartCheckoutButton}
                    isBuyOrCheckoutPage={isBuyOrCheckoutPage}
                    isBuyPage={isBuyPage}
                />
                <WindowSizeCalc />
                <div role="main" id="main">
                    {/* Keyed, so that navigating to another page clears an error */}
                    <PageErrorBoundary key={location.pathname}>
                        {children}
                    </PageErrorBoundary>
                    {minimalFooter ? (
                        <FooterMinimal
                            isBuyOrCheckoutPage={isBuyOrCheckoutPage}
                        />
                    ) : (
                        <Footer />
                    )}
                    {hasMounted && (
                        <React.Suspense fallback={null}>
                            <AdminColorPicker />
                        </React.Suspense>
                    )}
                </div>
                <SiteOverlay />
                {hasMounted && (
                    <React.Suspense fallback={null}>
                        <Modal />
                        <GridOverlay />
                    </React.Suspense>
                )}
                <ErrorOverlay />
            </DebouncedCartUpdateProvider>
        </>
    );
}

function LayoutWrapper(props: KlimPageProps): React.ReactElement {
    const [, setPageHasMounted] = useGlobalState('pageHasMounted');
    const {
        fontFamilyGroup,
        fontFamily,
        customFontsPostPage,
        isBuyPage,
        isInUsePostPage,
        colorSchemes: contextColorSchemes,
    } = props.pageContext;

    // Let the global state know that the page has mounted
    React.useEffect(() => {
        setPageHasMounted(true);
    }, []);

    // Provide colour schemes and add background luminance
    const colorSchemes = React.useMemo(
        () =>
            // The pageContext colorSchemes always trump any others
            contextColorSchemes ||
            (customFontsPostPage && [
                getColorThemeFromBaseColors({
                    backgroundColor: customFontsPostPage.backgroundColor,
                    foregroundColor: customFontsPostPage.foregroundColor,
                    highlightColor: customFontsPostPage.highlightColor,
                }),
            ]) ||
            (fontFamily?.fontFamilyGroup || fontFamilyGroup)?.colorSchemes.map(
                (colorScheme) =>
                    getColorThemeFromBaseColors({
                        backgroundColor: colorScheme.backgroundColor,
                        foregroundColor: colorScheme.foregroundColor,
                        highlightColor: colorScheme.highlightColor,
                    }),
            ) ||
            defaultColorSchemes,
        [
            contextColorSchemes,
            customFontsPostPage,
            fontFamily?.fontFamilyGroup,
            fontFamilyGroup,
        ],
    );

    const colorSchemePersistKey = React.useMemo(
        () =>
            (isBuyPage && 'buy-page') ||
            (isInUsePostPage && 'in-use-post-page') ||
            (customFontsPostPage && customFontsPostPage.slug) ||
            (fontFamily && `${fontFamily.fontFamilyGroup.slug}-0`) ||
            // Only persist to font family groups without campaign, hence the 0
            // which will differ from the `withCampaign`.
            (fontFamilyGroup &&
                `${fontFamilyGroup.slug}-${
                    fontFamilyGroup.campaign ? 'withCampaign' : '0'
                }`),
        [
            customFontsPostPage,
            fontFamily,
            fontFamilyGroup,
            isBuyPage,
            isInUsePostPage,
        ],
    );

    return (
        <React.StrictMode>
            <ApolloProvider client={runtimeApolloClient}>
                <PageQueryContext value={props.pageContext}>
                    <ColorSchemeProvider
                        colorSchemes={colorSchemes}
                        persistKey={colorSchemePersistKey}
                    >
                        <TooltipProvider delayDuration={0}>
                            <Layout {...props} />
                        </TooltipProvider>
                    </ColorSchemeProvider>
                </PageQueryContext>
            </ApolloProvider>
        </React.StrictMode>
    );
}

export default React.memo(LayoutWrapper);
